// The CTA sunburst's center overlay (ChartOverlayBig.svg / ChartOverlay.svg) has two
// separate soft-shadow mechanisms:
//   - .overlay's CSS `filter: drop-shadow(...)` (CtaSunburstChart.vue) - .overlay-big has
//     a `body.rendering { filter: none }` reset, but .overlay does not.
//   - ChartOverlayBig.svg's own internal SVG <filter> (feGaussianBlur/feFlood), baked into
//     the asset itself, entirely separate from the CSS filter above.
// Both render as a heavy dark blob instead of a soft shadow once domToImage rasterizes the
// chart onto a <canvas> (observed in Firefox). Neither is desirable in the PDF export, so
// both are neutralized before capture and restored immediately after so the live report is
// unaffected.

const shadowlessOverlaySrcCache = new Map<string, string>();

export const getShadowlessOverlaySrc = async (originalSrc: string): Promise<string> => {
    const cached = shadowlessOverlaySrcCache.get(originalSrc);
    if (cached) {
        return cached;
    }
    try {
        const response = await fetch(originalSrc);
        if (!response.ok) {
            throw new Error(`fetch failed with status ${response.status}`);
        }
        const svgText = await response.text();
        const stripped = svgText.replace(/filter:\s*url\([^)]*\)\s*;?/g, '');
        const url = URL.createObjectURL(new Blob([stripped], { type: 'image/svg+xml' }));
        shadowlessOverlaySrcCache.set(originalSrc, url);
        return url;
    } catch (error) {
        // Fetching/stripping the asset failed (network blip, CORS, service worker
        // interference - this app is a PWA). Falling back to the original src means this
        // one render keeps the internal-SVG-filter shadow, but the capture - and the rest
        // of the PDF export - still completes instead of throwing and breaking print()
        // entirely. Not cached, so a later successful attempt isn't stuck with the fallback.
        console.error('getShadowlessOverlaySrc: falling back to original overlay asset', error);
        return originalSrc;
    }
};

export interface OverlayShadowFixHandle {
    restore: () => void;
}

export const applyCtaOverlayShadowFix = async (container: HTMLElement): Promise<OverlayShadowFixHandle> => {
    const shadowedOverlays = Array.from(container.querySelectorAll<HTMLElement>('.overlay-big, .overlay'));
    shadowedOverlays.forEach((overlay) => (overlay.style.filter = 'none'));

    const overlayBigImages = Array.from(container.querySelectorAll<HTMLImageElement>('img.overlay-big'));
    const originalOverlayBigSrcs = overlayBigImages.map((img) => img.src);
    await Promise.all(
        overlayBigImages.map(async (img) => {
            img.src = await getShadowlessOverlaySrc(img.src);
            await img.decode().catch(() => undefined);
        }),
    );

    return {
        restore: () => {
            shadowedOverlays.forEach((overlay) => (overlay.style.filter = ''));
            overlayBigImages.forEach((img, idx) => (img.src = originalOverlayBigSrcs[idx]));
        },
    };
};
